import { Injectable } from "@angular/core";
import { BlobStoragePort } from "@partikelart/shared/util-blob-storage/core";

const STANDARD_ASSETS: Record<string, string> = {
	"part/11111111-0000-4000-8000-000000000001": "assets/images/scheibe.jpg",
	"part/11111111-0000-4000-8000-000000000003": "assets/images/flansch.jpg",
	"collector/0b3a3230-7d21-4d44-8dec-57de0343acd0": "assets/images/stamp_big.png",
	"collector/e6ca3e95-89a5-4284-b613-f9452d818b29": "assets/images/stamp_medium.png",
	"collector/e796848d-4918-47b7-82bc-6bdafbc40c75": "assets/images/stamp.png",
	"collector/d8dc5c8c-820a-4a6e-9ab1-c98d229aadfd": "assets/images/filter.png",
	"collector/35ea6590-4dd5-46cd-be6c-35497d2942c2": "assets/images/normal.png",
};

@Injectable()
export class DemoBlobStorageService implements BlobStoragePort {
	private readonly dbName = "PartikelDemoBlobStorage";
	private readonly storeName = "blobs";

	private async getDb(): Promise<IDBDatabase> {
		return new Promise((resolve, reject) => {
			const request = indexedDB.open(this.dbName, 1);
			request.onupgradeneeded = () => {
				const db = request.result;
				if (!db.objectStoreNames.contains(this.storeName)) {
					db.createObjectStore(this.storeName);
				}
			};
			request.onsuccess = () => resolve(request.result);
			request.onerror = () => reject(request.error);
		});
	}

	async upload(file: File | Blob, path: string): Promise<string> {
		if (this.isStandardAsset(path)) {
			console.warn(`[BlobStorage] Attempted to overwrite standard asset: ${path}. Operation ignored.`);
			return path;
		}

		const db = await this.getDb();
		return new Promise((resolve, reject) => {
			const tx = db.transaction(this.storeName, "readwrite");
			const store = tx.objectStore(this.storeName);
			const request = store.put(file, path);
			request.onsuccess = () => resolve(path);
			request.onerror = () => reject(request.error);
		});
	}

	async download(path: string): Promise<Blob> {
		if (this.isStandardAsset(path)) {
			const assetPath = STANDARD_ASSETS[path];
			const response = await fetch(assetPath);
			if (!response.ok) {
				throw new Error(`[BlobStorage] Failed to fetch standard asset at ${assetPath}`);
			}
			return await response.blob();
		}

		const db = await this.getDb();
		return new Promise((resolve, reject) => {
			const tx = db.transaction(this.storeName, "readonly");
			const store = tx.objectStore(this.storeName);
			const request = store.get(path);
			request.onsuccess = () => {
				if (request.result) {
					resolve(request.result);
				} else {
					reject(new Error(`[BlobStorage] File not found in demo storage: ${path}`));
				}
			};
			request.onerror = () => reject(request.error);
		});
	}

	async delete(path: string): Promise<void> {
		if (this.isStandardAsset(path)) {
			console.warn(`[BlobStorage] Attempted to delete standard asset: ${path}. Operation ignored.`);
			return;
		}

		const db = await this.getDb();
		return new Promise((resolve, reject) => {
			const tx = db.transaction(this.storeName, "readwrite");
			const store = tx.objectStore(this.storeName);
			const request = store.delete(path);
			request.onsuccess = () => resolve();
			request.onerror = () => reject(request.error);
		});
	}

	async exists(path: string): Promise<boolean> {
		if (this.isStandardAsset(path)) {
			return true;
		}

		const db = await this.getDb();
		return new Promise((resolve, reject) => {
			const tx = db.transaction(this.storeName, "readonly");
			const store = tx.objectStore(this.storeName);
			const request = store.count(path);
			request.onsuccess = () => resolve(request.result > 0);
			request.onerror = () => reject(request.error);
		});
	}

	private isStandardAsset(path: string): boolean {
		return path in STANDARD_ASSETS;
	}
}
